import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { Icon } from "../components/Icons";
import { fLong } from "../lib/format";
import { cleanBill, fetchMissing, filterMissing, missingYears, type MissingShow } from "../lib/publicPage";

/** Instagram receives flyer submissions. */
export const FLYER_SUBMIT_URL = "https://www.instagram.com/showsneverforgotten/";

/**
 * Flyers wanted: PUBLIC, no nav, and kept out of search engines. Search and year live in the URL so a filtered link can
 * be shared from Instagram.
 */
export default function FlyersWanted() {
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") ?? "";
  const year = sp.get("year");
  const [shows, setShows] = useState<MissingShow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    document.title = "Flyers wanted";
    return () => meta.remove();
  }, []);

  useEffect(() => {
    fetchMissing().then(setShows, (e: Error) => setError(e.message));
  }, []);

  const set = (k: string, v: string | null) =>
    setSp((p) => {
      if (v) p.set(k, v);
      else p.delete(k);
      return p;
    }, { replace: true });
  const list = useMemo(() => filterMissing(shows ?? [], q, year), [shows, q, year]);
  const years = useMemo(() => missingYears(shows ?? []), [shows]);

  return (
    <div className="app">
      <div className="pub-hdr">
        <h1>
          Flyers <i>wanted</i>
        </h1>
        <p>{shows ? `${shows.length} shows I was at and never found the flyer for. Recognize one?` : "Shows I was at and never found the flyer for."}</p>
      </div>
      <div className="page" style={{ paddingTop: 8 }}>
        <a className="cta" href={FLYER_SUBMIT_URL} target="_blank" rel="nofollow noopener noreferrer">
          <b style={{ font: "900 22px var(--disp)" }}>DM</b>
          <span>
            Got one of these? Send it to <b>@showsneverforgotten</b> on Instagram.
          </span>
        </a>
        <div className="toolbar">
          <label className="search">
            <Icon name="search" />
            <input value={q} onChange={(e) => set("q", e.target.value || null)} placeholder="Band or venue" aria-label="Search" />
          </label>
          <div className="chips">
            <button type="button" aria-pressed={!year} onClick={() => set("year", null)}>
              All years
            </button>
            {years.map((y) => (
              <button type="button" key={y} aria-pressed={year === y} onClick={() => set("year", y)}>
                {y}
              </button>
            ))}
          </div>
        </div>
        {error && <p className="error">{error}</p>}
        {!shows && !error && <p className="loading">Loading…</p>}
        <div className="pub-grid">
          {list.map((s) => (
            <div className="pcard" key={`${s.date}${s.venue}${s.bands}`}>
              <span className="pd">{fLong(s.date)}</span>
              {s.event && (
                <span>
                  <span className="pill">{s.event}</span>
                </span>
              )}
              <span className="pb">{cleanBill(s.bands) || <em>(no lineup recorded)</em>}</span>
              <span className="pv">{[s.venue, s.state].filter(Boolean).join(" · ")}</span>
            </div>
          ))}
          {shows && !list.length && <p className="muted">None match.</p>}
        </div>
      </div>
    </div>
  );
}
